import { useApolloClient, useQuery } from '@apollo/react-hooks';
import classNames from 'classnames';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';
import actionCable from 'actioncable';
import { useTranslation } from 'react-i18next';

import { useBusiness, useUser } from 'graphql/graph-hooks';
import { CONVERSATION_FRAGMENT } from 'graphql/fragments/conversation-fragment';
import { ADMIN_CONVERSATION_FRAGMENT } from 'graphql/fragments/admin-conversation-fragment';
import { GET_MESSAGES_PAGINATED } from 'graphql/queries/messages-query';
import { APIConstants } from 'helpers/constants';
import { useFetchMore as useFetchMoreMessages } from 'components/global-hooks';
import S from './chat-msg-area.module.scss';
import { ChatMsgBody } from './chat-msg-body';
import { ChatMsgFooter } from './chat-msg-footer';
import { ChatMsgHead } from './chat-msg-head';
import * as PageChatCtx from '../chat-ctx';
import type { ConversationTypes, MsgItemType, MsgSender, TargetSenderKeys } from 'types/conversation';
import i18n from 'i18n';

type ChatMsgAreaProps = {
  isAdminChat: boolean;
  avatarOnClick: (dancerId: string) => void;
  isLoading: boolean;
  conversations: ConversationTypes[];
  setConversations: Dispatch<SetStateAction<ConversationTypes[]>>;
};

type msgReduceOutput = {
  firstUnreadId: string | null;
  lastMsgId: string | null;
};

type actionCableMsg = {
  type: string;
  message: MsgItemType;
};

type receivedType = {
  event: actionCableMsg;
};

const { socket_protocol, domain } = APIConstants;
const SOCKET_URL = `${socket_protocol}://${domain}/cable?auth_token=`;

export const ChatMsgArea: React.FC<ChatMsgAreaProps> = ({
  isAdminChat,
  avatarOnClick,
  isLoading,
  conversations,
  setConversations,
}): React.ReactElement => {
  const CLIENT = useApolloClient();
  const { t } = useTranslation();
  const {
    dispatch,
    state: { firstUnreadId, conversationId, chatBizId: businessId },
  } = PageChatCtx.usePageChatCtx();
  const { admin: isAdmin } = useUser();
  // TODO: should be possible to know acceptable types, no ?
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const CABLE = useRef<any>(actionCable.createConsumer(`${SOCKET_URL}${localStorage.getItem('token')}`));

  const { id: currentBusinessId } = useBusiness();

  const {
    isLoading: isFetchMoreLoading,
    fetchMore: fetchMoreMessages,
    error: fetchMoreError,
  } = useFetchMoreMessages('messages_paginated', true);

  const { DATA, clientError } = useMemo(() => {
    let data: { user: TargetSenderKeys } | null = null;
    let error: string | null = null;
    try {
      data = isLoading
        ? null
        : isAdminChat
        ? CLIENT.readFragment({
            fragment: ADMIN_CONVERSATION_FRAGMENT,
            fragmentName: 'adminConversationFragment',
            id: `Conversation:${conversationId}`,
            variables: {
              isAdmin,
            },
          })
        : CLIENT.readFragment({
            fragment: CONVERSATION_FRAGMENT,
            fragmentName: 'conversationFragment',
            id: `Conversation:${conversationId}`,
            variables: {
              isAdmin,
              blockedByBusinessId: currentBusinessId,
              chatBizId: businessId,
              inGroupBusinessId: currentBusinessId,
            },
          });
    } catch (err: unknown) {
      error = i18n.t('errors.server.badLink');
      // Remove all parameters from URL since current link is not valid anymore
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
    return { DATA: data, clientError: error };
  }, [isLoading, isAdminChat, CLIENT, conversationId, isAdmin, currentBusinessId, businessId]);

  const TARGET = DATA?.user;

  const {
    loading: processingLoad,
    data,
    error,
    fetchMore,
  } = useQuery(GET_MESSAGES_PAGINATED, {
    skip: !conversationId || !TARGET,
    variables: { conversationId: conversationId, last: 20 },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const MESSAGES: MsgItemType[] = data?.messages_paginated.nodes ?? [];
      if (MESSAGES.length > 0) {
        const { firstUnreadId, lastMsgId } = MESSAGES.reduce<msgReduceOutput>(
          (acm, { id, sender, read }) => {
            return !read && sender?.id === TARGET?.id
              ? {
                  firstUnreadId: acm.firstUnreadId || id,
                  lastMsgId: id,
                }
              : acm;
          },
          { firstUnreadId: null, lastMsgId: null }
        );
        dispatch(PageChatCtx.setChatFields({ firstUnreadId, lastMsgId }));
      }
    },
  });

  const handleMessageReceived = useCallback(
    (message: MsgItemType) => {
      const MSG = {
        __typename: 'Message',
        id: message.id.toString(),
        body: message.body,
        updated_at: message.updated_at,
        created_at: message.created_at,
        read: message.read,
        sender: {
          __typename: 'User',
          id: message.sender.id.toString(),
          name: message.sender.name,
        },
      };

      const CONVERSATION = conversations.find(({ id }) => id === conversationId);

      const user = !CONVERSATION ? TARGET : CONVERSATION.user;

      setConversations((prevConversations: ConversationTypes[]): ConversationTypes[] => {
        const updatedConversations = [
          {
            __typename: 'Conversation',
            id: conversationId as string,
            updated_at: MSG.created_at,
            last_message: MSG,
            last_message_sent: MSG.created_at,
            business_unread_messages_count: 0,
            user: user as TargetSenderKeys,
          },
          ...prevConversations.filter(({ id }) => id !== conversationId),
        ];

        return updatedConversations;
      });

      if (MSG.sender.id === user?.id) {
        dispatch(
          PageChatCtx.setChatFields({
            firstUnreadId: conversationId ? null : firstUnreadId || MSG.id,
            lastMsgId: MSG.id,
            scrollToBottom: true,
          })
        );

        let cachedData;
        try {
          cachedData = CLIENT.readQuery({
            query: GET_MESSAGES_PAGINATED,
            variables: { conversationId, last: 20 },
          });
        } catch {
          // No cached messages yet for this conversation; nothing to append to.
          return;
        }
        if (!cachedData?.messages_paginated) return;

        CLIENT.writeQuery({
          query: GET_MESSAGES_PAGINATED,
          variables: { conversationId, last: 20 },
          data: {
            messages_paginated: {
              __typename: 'MessageConnection',
              nodes: [...cachedData.messages_paginated.nodes, MSG],
              pageInfo: cachedData.messages_paginated.pageInfo,
            },
          },
        });
      }
    },
    [conversationId, setConversations, conversations, TARGET, firstUnreadId, dispatch, CLIENT, GET_MESSAGES_PAGINATED]
  );

  const createCable = useCallback(() => {
    if (!conversationId) return;
    const LAST_CONVO = CABLE.current.subscriptions.subscriptions[0];
    if (LAST_CONVO) {
      CABLE.current.subscriptions.remove(LAST_CONVO);
    }

    CABLE.current.subscriptions.create(
      {
        channel: 'ConversationChannel',
        data: { conversation_id: conversationId },
      },
      {
        received: ({ event: { type, message } }: receivedType) => {
          if (type === 'Message#Create') {
            handleMessageReceived(message);
          }
        },
      }
    );
  }, [conversationId, handleMessageReceived]);

  useEffect(() => {
    const CONNECTION = CABLE.current;
    if (conversationId) {
      createCable();
    }
    return () => CONNECTION.disconnect();
  }, [conversationId, createCable]);

  if (!conversationId) {
    return (
      <div className={classNames(S.chatMsgArea, { [S.isAdmin]: isAdminChat })}>
        <h3 className={S.warnMsg}>{t('chatPage.select_dancer_in_panel')}</h3>
      </div>
    );
  }

  const handleOnScrollTop = () => {
    if (!data?.messages_paginated?.pageInfo?.hasPreviousPage) {
      return;
    }
    dispatch(PageChatCtx.setChatFields({ scrollToBottom: false }));
    fetchMoreMessages(fetchMore, { before: data.messages_paginated.pageInfo.startCursor });
  };

  const loadingError =
    clientError ||
    ((error || (!isLoading && !processingLoad && !TARGET && t('chatPage.error_loading_conversation'))) as string);

  return (
    <div className={classNames(S.chatMsgArea, { [S.isAdmin]: isAdminChat })}>
      {!clientError && <ChatMsgHead {...(TARGET as TargetSenderKeys)} avatarOnClick={avatarOnClick} />}
      <ChatMsgBody
        targetId={TARGET?.id as string}
        isLoading={processingLoad || isLoading}
        isFetchMoreLoading={isFetchMoreLoading}
        messages={data?.messages_paginated.nodes}
        conversationId={conversationId}
        onScrollTop={handleOnScrollTop}
        error={fetchMoreError || loadingError}
      />
      {((isAdminChat && isAdmin) || (!isAdminChat && !isAdmin)) && !loadingError && (
        <ChatMsgFooter target={TARGET as MsgSender} conversationId={conversationId} isAdmin={isAdmin} />
      )}
    </div>
  );
};
